// src/components/Organizations/Marketing/SocialMediaCampaignCreator.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  Alert,
  Chip,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  ChevronRight as NextIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { generateSocialMediaPosts, generateSocialMediaImage } from '../../../services/stabilityAIService-new';
import { collection, addDoc, getDocs, query, where, orderBy, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface EventDetails {
  title: string;
  description: string;
  type: string;
  date: Date;
  location: string;
}

interface SocialMediaCampaignCreatorProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  eventId: string;
  eventDetails: EventDetails;
  existingCampaign?: Campaign;
}

interface SocialPost {
  platform: string;
  content: string;
  imagePrompt: string;
  imageUrl?: string;
  hashtags: string[];
}

interface Campaign {
  id: string;
  name: string;
  eventId: string;
  organizationId: string;
  platforms: string[];
  posts: SocialPost[];
  createdAt: Timestamp;
  status: 'draft' | 'scheduled' | 'published';
  targetAudience?: string;
}

// Helper function to safely convert data URLs to blobs
const dataURLtoBlob = (dataurl: string): Blob => {
  try {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  } catch (err) {
    console.error('Error converting data URL to blob:', err);
    // Return a small empty blob as fallback
    return new Blob([''], { type: 'image/png' });
  }
};

const SocialMediaCampaignCreator: React.FC<SocialMediaCampaignCreatorProps> = ({
  open,
  onClose,
  organizationId,
  eventId,
  eventDetails,
  existingCampaign
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState(`${eventDetails.title} Campaign`);
  const [targetAudience, setTargetAudience] = useState('');
  const [platforms, setPlatforms] = useState({
    facebook: true,
    instagram: true,
    twitter: true,
    linkedin: false
  });
  const [postsPerPlatform, setPostsPerPlatform] = useState(1);
  const [generatedPosts, setGeneratedPosts] = useState<SocialPost[]>([]);
  const [editedPosts, setEditedPosts] = useState<SocialPost[]>([]);
  const [savedCampaigns, setSavedCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  
  // Load existing campaigns when the component opens
  useEffect(() => {
    if (open && organizationId && eventId) {
      fetchCampaigns();
    }
  }, [open, organizationId, eventId]);
  
  // Initialize state with existing campaign data if available
  useEffect(() => {
    if (existingCampaign) {
      setCampaignName(existingCampaign.name);
      setTargetAudience(existingCampaign.targetAudience || '');
      
      // Set platforms
      const campaignPlatforms = existingCampaign.platforms || [];
      setPlatforms({
        facebook: campaignPlatforms.includes('facebook'),
        instagram: campaignPlatforms.includes('instagram'),
        twitter: campaignPlatforms.includes('twitter'),
        linkedin: campaignPlatforms.includes('linkedin')
      });
      
      // Set posts
      if (existingCampaign.posts) {
        setGeneratedPosts(existingCampaign.posts);
        setEditedPosts(existingCampaign.posts);
      }
      
      // Skip to the review step when editing
      setActiveStep(2);
    }
  }, [existingCampaign]);
  
  const fetchCampaigns = async () => {
     setLoadingCampaigns(true);
     try {
        // Query campaigns for this organization and event
        const campaignsRef = collection(db, 'organizations', organizationId, 'marketingCampaigns');
        const q = query(
           campaignsRef,
           where('eventId', '==', eventId),
           orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const campaigns: Campaign[] = [];

        querySnapshot.forEach((doc) => {
           campaigns.push({ id: doc.id, ...doc.data() } as Campaign);
        });

        setSavedCampaigns(campaigns);
     } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load existing campaigns');
     } finally {
        setLoadingCampaigns(false);
     }
  };

  const handlePlatformChange = (platform: keyof typeof platforms) => {
     setPlatforms({
        ...platforms,
        [platform]: !platforms[platform],
     });
  };

  const handleNext = () => {
     if (activeStep === 0) {
        // Validate first step
        if (!campaignName.trim()) {
           setError('Please enter a campaign name');
           return;
        }

        // Make sure at least one platform is selected
        if (
           !platforms.facebook &&
           !platforms.instagram &&
           !platforms.twitter &&
           !platforms.linkedin
        ) {
           setError('Please select at least one social media platform');
           return;
        }
     }

     setError(null);

     if (activeStep === 1) {
        // Generate social media posts
        generatePosts();
     } else {
        setActiveStep((prevStep) => prevStep + 1);
     }
  };

  const handleBack = () => {
     setActiveStep((prevStep) => prevStep - 1);
  };

  const generatePosts = async () => {
     setLoading(true);
     setError(null);

     try {
        const selectedPlatforms = Object.entries(platforms)
           .filter(([_, selected]) => selected)
           .map(([platform]) => platform);

        if (selectedPlatforms.length === 0) {
           throw new Error('No platforms selected');
        }

        const generatedContent = await generateSocialMediaPosts({
           platforms: selectedPlatforms,
           eventDetails,
           postsPerPlatform,
        });

        if (!generatedContent || generatedContent.length === 0) {
           throw new Error('No content was generated');
        }

        setGeneratedPosts(generatedContent);
        setEditedPosts(generatedContent);
        setActiveStep(2);
     } catch (err) {
        console.error('Error generating posts:', err);
        setError(
           'Failed to generate social media posts. Please try again. ' +
              (err instanceof Error ? err.message : '')
        );
     } finally {
        setLoading(false);
     }
  };
  
  const handlePostEdit = (index: number, content: string) => {
    const newPosts = [...editedPosts];
    newPosts[index].content = content;
    setEditedPosts(newPosts);
  };
  
  const handleHashtagAdd = (index: number, hashtag: string) => {
    if (!hashtag.trim()) return;
    
    const newPosts = [...editedPosts];
    if (!newPosts[index].hashtags.includes(hashtag)) {
      newPosts[index].hashtags.push(hashtag);
      setEditedPosts(newPosts);
    }
  };
  
  const handleHashtagRemove = (index: number, hashtag: string) => {
    const newPosts = [...editedPosts];
    newPosts[index].hashtags = newPosts[index].hashtags.filter(h => h !== hashtag);
    setEditedPosts(newPosts);
  };
  
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook': return <FacebookIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'linkedin': return <LinkedInIcon />;
      default: return <AutoAwesomeIcon />;
    }
  };
  
  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'facebook': return 'Facebook';
      case 'instagram': return 'Instagram';
      case 'twitter': return 'Twitter';
      case 'linkedin': return 'LinkedIn';
      default: return platform;
    }
  };
  
  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'facebook': return '#1877F2';
      case 'instagram': return '#E4405F';
      case 'twitter': return '#1DA1F2';
      case 'linkedin': return '#0A66C2';
      default: return '#1976D2';
    }
  };
  
  const handleFinish = async () => {
    // Prepare campaign data
    setLoading(true);
    setError(null);
    
    try {
       // Make a copy of the posts to avoid modifying the original state
       let postsToSave = [...editedPosts];

       try {
          // Process any image URLs that are data URLs (base64)
          const processedPosts = await Promise.all(
             postsToSave.map(async (post, index) => {
                // Skip processing if no imageUrl exists
                if (!post.imageUrl) {
                   return {
                      ...post,
                      imageUrl: '/assets/images/placeholder-event.jpg',
                   };
                }

                // If the post image URL is a data URL, we need to upload it to Firebase Storage
                if (post.imageUrl.startsWith('data:')) {
                   try {
                      // Convert data URL to blob directly with helper function
                      const blob = dataURLtoBlob(post.imageUrl);

                      // Create a unique filename
                      const filename = `${Date.now()}_${post.platform}_${index}.png`;

                      // Upload to Firebase Storage
                      const storage = getStorage();
                      const storageRef = ref(
                         storage,
                         `organizations/${organizationId}/campaigns/${eventId}/${filename}`
                      );

                      await uploadBytes(storageRef, blob);

                      // Get the download URL
                      const downloadURL = await getDownloadURL(storageRef);

                      // Return the updated post with the new URL
                      return {
                         ...post,
                         imageUrl: downloadURL,
                      };
                   } catch (err) {
                      console.error('Error uploading image:', err);
                      // If upload fails, use a placeholder instead
                      return {
                         ...post,
                         imageUrl: '/assets/images/placeholder-event.jpg',
                      };
                   }
                }

                // If it's not a data URL, keep it as is
                return post;
             })
          );

          // Update the posts to save with the processed ones
          postsToSave = processedPosts;
       } catch (processErr) {
          console.error('Error processing images:', processErr);
          // Continue with the original posts, but strip out any data URLs
          postsToSave = postsToSave.map((post) => {
             if (post.imageUrl && post.imageUrl.startsWith('data:')) {
                return {
                   ...post,
                   imageUrl: '/assets/images/placeholder-event.jpg',
                };
             }
             return post;
          });
       }

       // Prepare the campaign data with the processed posts
       const campaignData = {
          name: campaignName,
          eventId,
          organizationId,
          targetAudience: targetAudience || null,
          platforms: Object.keys(platforms).filter(
             (key) => platforms[key as keyof typeof platforms]
          ),
          posts: postsToSave.map((post) => ({
             ...post,
             content: post.content,
             hashtags: post.hashtags,
             platform: post.platform,
             imagePrompt: post.imagePrompt,
             // Only include these safe fields to avoid serialization issues
             imageUrl: post.imageUrl || '/assets/images/placeholder-event.jpg',
             createdAt: new Date(),
          })),
          createdAt: new Date(),
          eventDetails: {
             title: eventDetails.title,
             type: eventDetails.type,
             date:
                eventDetails.date instanceof Date ? eventDetails.date : new Date(eventDetails.date),
             location: eventDetails.location,
          },
          status: 'draft',
       };

       // Save to Firestore - update if editing, create new if not
       if (existingCampaign) {
          // Update existing campaign
          const campaignRef = doc(
             db,
             'organizations',
             organizationId,
             'marketingCampaigns',
             existingCampaign.id
          );
          await updateDoc(campaignRef, campaignData);
       } else {
          // Create new campaign
          const campaignRef = collection(db, 'organizations', organizationId, 'marketingCampaigns');
          const docRef = await addDoc(campaignRef, campaignData);
       }

       // Show success message
       setSuccess('Campaign saved successfully');

       // Refresh campaigns list
       await fetchCampaigns();

       // Close the dialog after a short delay
       setTimeout(() => {
          onClose();
       }, 1500);
    } catch (err) {
      console.error('Error saving campaign:', err);
      
      // Provide a more helpful error message based on the error type
      let errorMessage = 'Failed to save campaign';
      
      if (err instanceof Error) {
        // If it's a Firestore error related to invalid data
        if (err.message.includes('invalid nested entity') || 
            err.message.includes('Provided document data') ||
            err.message.includes('Unsupported field value')) {
          errorMessage = 'The campaign contains data that cannot be saved. Try regenerating the images.';
        } else {
          errorMessage = `Failed to save campaign: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle creating a new campaign
  const handleCreateNewCampaign = () => {
    setCampaignName(`${eventDetails.title} Campaign`);
    setTargetAudience('');
    setPlatforms({
      facebook: true,
      instagram: true,
      twitter: true,
      linkedin: false
    });
    setPostsPerPlatform(1);
    setGeneratedPosts([]);
    setEditedPosts([]);
    setActiveStep(0);
  };
  
  // Render the campaigns list
  const renderCampaignsList = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Existing Campaigns
      </Typography>
      {loadingCampaigns ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : savedCampaigns.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No campaigns found for this event. Create your first campaign!
        </Alert>
      ) : (
        <List>
          {savedCampaigns.map((campaign) => (
            <Paper 
              key={campaign.id} 
              elevation={1} 
              sx={{ mb: 2, overflow: 'hidden' }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {campaign.name}
                </Typography>
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    Created: {campaign.createdAt.toDate().toLocaleDateString()}
                  </Typography>
                  <Chip 
                    label={campaign.status.toUpperCase()} 
                    size="small" 
                    color={campaign.status === 'published' ? 'success' : 'default'}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {campaign.platforms.map(platform => {
                    const icon = getPlatformIcon(platform);
                    return (
                      <Chip
                        key={platform}
                        icon={icon}
                        label={getPlatformName(platform)}
                        size="small"
                        sx={{
                          color: getPlatformColor(platform),
                          borderColor: getPlatformColor(platform)
                        }}
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
                <Grid container spacing={2}>
                  {campaign.posts.slice(0, 3).map((post, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        {post.imageUrl && (
                          <Box sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              height={140}
                              image={post.imageUrl}
                              alt={`Post ${index + 1}`}
                              sx={{ objectFit: 'cover' }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: getPlatformColor(post.platform),
                                color: 'white',
                                borderRadius: '50%',
                                width: 30,
                                height: 30,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {getPlatformIcon(post.platform)}
                            </Box>
                          </Box>
                        )}
                        <CardContent sx={{ height: 100, overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap>
                            {post.content.substring(0, 100)}...
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {campaign.posts.length > 3 && (
                  <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 1 }}>
                    +{campaign.posts.length - 3} more posts
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </List>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleCreateNewCampaign}
        >
          Create New Campaign
        </Button>
      </Box>
    </Box>
  );
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <AutoAwesomeIcon sx={{ mr: 1.5, verticalAlign: 'text-bottom' }} />
            {existingCampaign ? 'Edit Social Media Campaign' : 'Create Social Media Campaign'}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Show saved campaigns before the stepper */}
        {activeStep === 0 && savedCampaigns.length > 0 && renderCampaignsList()}
        
        {/* Only show the stepper if we're creating a new campaign */}
        {(savedCampaigns.length === 0 || activeStep > 0) && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Campaign Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Configuration</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review & Edit</StepLabel>
            </Step>
            <Step>
              <StepLabel>Schedule & Publish</StepLabel>
            </Step>
          </Stepper>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Success Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess(null)}
          message={success}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
        
        {activeStep === 0 && savedCampaigns.length === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Campaign Details</Typography>
            
            <TextField
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
            
            <TextField
              label="Target Audience (optional)"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="e.g., Professionals aged 25-45, interested in networking"
              helperText="Describe your target audience to help generate more relevant content"
            />
            
            <Box sx={{ mt: 3 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Select Platforms</FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={<Checkbox checked={platforms.facebook} onChange={() => handlePlatformChange('facebook')} />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FacebookIcon color="primary" sx={{ mr: 1 }} /> Facebook</Box>}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={platforms.instagram} onChange={() => handlePlatformChange('instagram')} />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}><InstagramIcon sx={{ mr: 1, color: '#E4405F' }} /> Instagram</Box>}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={platforms.twitter} onChange={() => handlePlatformChange('twitter')} />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}><TwitterIcon sx={{ mr: 1, color: '#1DA1F2' }} /> Twitter</Box>}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={platforms.linkedin} onChange={() => handlePlatformChange('linkedin')} />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LinkedInIcon sx={{ mr: 1, color: '#0A66C2' }} /> LinkedIn</Box>}
                  />
                </FormGroup>
              </FormControl>
            </Box>
            
            <TextField
              label="Posts per Platform"
              type="number"
              value={postsPerPlatform}
              onChange={(e) => setPostsPerPlatform(Math.max(1, Math.min(5, parseInt(e.target.value))))}
              fullWidth
              margin="normal"
              inputProps={{ min: 1, max: 5 }}
              helperText="Number of posts to generate for each selected platform (1-5)"
            />
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Campaign Configuration</Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Event Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Event Title"
                    value={eventDetails.title}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Event Type"
                    value={eventDetails.type}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Event Date"
                    value={new Date(eventDetails.date).toLocaleDateString()}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Event Location"
                    value={eventDetails.location}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Based on your selected platforms and event details, the AI will generate {
                Object.values(platforms).filter(Boolean).length * postsPerPlatform
              } unique posts optimized for each platform.
            </Alert>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ width: '100%', mb: 1 }}>Selected Platforms:</Typography>
              {platforms.facebook && (
                <Chip icon={<FacebookIcon />} label="Facebook" color="primary" variant="outlined" />
              )}
              {platforms.instagram && (
                <Chip icon={<InstagramIcon />} label="Instagram" sx={{ color: '#E4405F', borderColor: '#E4405F' }} variant="outlined" />
              )}
              {platforms.twitter && (
                <Chip icon={<TwitterIcon />} label="Twitter" sx={{ color: '#1DA1F2', borderColor: '#1DA1F2' }} variant="outlined" />
              )}
              {platforms.linkedin && (
                <Chip icon={<LinkedInIcon />} label="LinkedIn" sx={{ color: '#0A66C2', borderColor: '#0A66C2' }} variant="outlined" />
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Click "Generate Posts" to create your social media campaign. This might take a moment while our AI creates optimized content for each platform.
            </Typography>
          </Box>
        )}
        
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Review & Edit Generated Content</Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the AI-generated posts for each platform. You can customize the text and hashtags before scheduling.
            </Typography>
            
            <Grid container spacing={3}>
              {editedPosts.map((post, index) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ 
                    p: 2,
                    borderLeft: `4px solid ${getPlatformColor(post.platform)}`,
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: getPlatformColor(post.platform),
                      color: 'white',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getPlatformIcon(post.platform)}
                    </Box>
                    
                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {getPlatformIcon(post.platform)}
                      <span style={{ marginLeft: '8px' }}>{getPlatformName(post.platform)}</span>
                    </Typography>
                    
                    {/* Display the generated image */}
                    {post.imageUrl && (
                      <Box sx={{ mb: 2, position: 'relative' }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Generated Image:
                        </Typography>
                        <Box
                          component="img"
                          src={post.imageUrl}
                          alt={`${getPlatformName(post.platform)} post image`}
                          sx={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            borderRadius: 1,
                            mb: 1
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AutoAwesomeIcon />}
                          sx={{ position: 'absolute', bottom: 8, right: 8 }}
                          onClick={async () => {
                            try {
                              setLoading(true);
                              const newImageUrl = await generateSocialMediaImage(post.imagePrompt, post.platform);
                              const newPosts = [...editedPosts];
                              newPosts[index] = { ...newPosts[index], imageUrl: newImageUrl };
                              setEditedPosts(newPosts);
                            } catch (err) {
                              console.error('Error regenerating image:', err);
                            } finally {
                              setLoading(false);
                            }
                          }}
                        >
                          Regenerate
                        </Button>
                      </Box>
                    )}
                    
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      value={post.content}
                      onChange={(e) => handlePostEdit(index, e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Hashtags:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {post.hashtags.map((hashtag, hIndex) => (
                          <Chip
                            key={hIndex}
                            label={`#${hashtag}`}
                            onDelete={() => handleHashtagRemove(index, hashtag)}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                        <TextField
                          placeholder="Add hashtag"
                          size="small"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleHashtagAdd(index, (e.target as HTMLInputElement).value);
                              (e.target as HTMLInputElement).value = '';
                            }
                          }}
                          sx={{ maxWidth: 150 }}
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom>Generated Image Prompt:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {post.imagePrompt}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>Schedule & Publish</Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Choose when to publish your social media posts. You can schedule them all at once or spread them out over time.
            </Alert>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  fullWidth
                  sx={{ height: '100%', py: 2 }}
                >
                  Schedule for Later
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                  fullWidth
                  sx={{ height: '100%', py: 2 }}
                  onClick={handleFinish}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Campaign'}
                </Button>
              </Grid>
            </Grid>
            
            <Typography variant="body2" color="text.secondary">
              Your campaign contains {editedPosts.length} posts across {
                Object.values(platforms).filter(Boolean).length
              } platforms. After saving, you can view and edit this campaign from the Marketing dashboard.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        
        {activeStep < 3 && (
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={loading}
            endIcon={loading ? <CircularProgress size={20} /> : <NextIcon />}
          >
            {activeStep === 1 ? 'Generate Posts' : 'Next'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SocialMediaCampaignCreator;